/**
 * AssistantMessage - Renders an assistant message with avatar and actions
 */

import { useState, useMemo, useRef } from 'react'
import { useTranslation } from '@/i18n'
import clsx from 'clsx'
import { Star, Copy, Check, ChevronDown, ChevronRight, Wrench, ExternalLink, Loader2, RefreshCw, ImageIcon, Calendar, Download, Search } from 'lucide-react'
import { useApiFeatureFlag } from '@/hooks/useFeatureFlag'
import ReactMarkdown, { type Options as ReactMarkdownOptions } from 'react-markdown'
import { toast } from 'sonner'
import 'katex/dist/katex.min.css'
import type { UIMessage, TextPart, ReasoningPart, FilePart, ToolCallPart, ToolResultPart, SourcePart, VersionInfo } from '../../types'
import { AGENTS, AGENT_COLORS, DEFAULT_AGENT } from '../../constants'
import { CodeBlock } from './CodeBlock'
import { FeedbackModal } from './FeedbackModal'
import { ReasoningPath } from './ReasoningPath'
import { ConsentRequestCard } from './ConsentRequestCard'
import { FollowUpQuestions } from './FollowUpQuestions'
import { VersionNavigator } from './VersionNavigator'
import { StructuredResponse } from './StructuredResponse'
import { SourcesFooter } from './SourcesFooter'
import { formatMessageTime } from '../../utils/messageConverter'
import { isHtmlChart } from '../../utils/chartSource'
import { downloadMessage, triggerBlobDownload } from '../../api/chatApiV2'
import type { DownloadFormat } from '../../api/chatApiV2'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
// Use the same math config as legacy chat for consistent rendering
// RE-ENABLED: Backend Pydantic validation not working consistently (Error 424 failures)
// Keep frontend preprocessing as safety net until backend validation is stable
import { latexToPlainText, convertMathNotation } from '@/utils/katexMathPreprocessor'
import { remarkPlugins, rehypePlugins, mergeMarkdownComponents } from '@/config/markdownConfig'
import type { EngineeringResponse } from '@/service/api'

// The shared plugin arrays in `@/config/markdownConfig` use the
// `[plugin, options]` tuple form, which TS widens to a heterogeneous
// array type that is not structurally assignable to react-markdown's
// `PluggableList` even though the values are valid at runtime. Narrow
// them to react-markdown's own prop types here so the ReactMarkdown
// call site type-checks. Root fix belongs in markdownConfig (see
// crossFileNeeds): annotate the exports as `PluggableList`.
const remarkPluginList = remarkPlugins as ReactMarkdownOptions['remarkPlugins']
const rehypePluginList = rehypePlugins as ReactMarkdownOptions['rehypePlugins']

/**
 * Extract structured response from message content.
 * Backend embeds engineering responses in format: ```json:structured_response\n{JSON}\n```
 *
 * PR 9 (gap closure): emit [STRUCTURED_FALLBACK] whenever the regex
 * fires and produces a parsed object. This tells the
 * response-formatting dashboard that the v2 typed-part path didn't
 * deliver a `data-structured-response` for this message and we fell
 * back to the markdown fence. Stops firing once formatting_v2
 * reaches 100% + the legacy parser is removed.
 */
const extractStructuredResponse = (content: string): EngineeringResponse | null => {
  // Don't parse incomplete content (might be still streaming)
  if (!content.includes('```json:structured_response')) {
    return null
  }

  const match = content.match(/```json:structured_response\n([\s\S]*?)\n```/)
  if (match) {
    try {
      const parsed = JSON.parse(match[1]) as EngineeringResponse
      // Lazy import keeps the observability module out of the hot
      // path for the common case (no fenced JSON in the message).
      void import('../../observability').then(({ logStructuredFallback }) => {
        logStructuredFallback({ contentLen: content.length })
      })
      return parsed
    } catch {
      // JSON parse failed - might be incomplete
      return null
    }
  }
  return null
}

/** File format icons – all paths, no <text> elements */
const PdfIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 2h8l6 6v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2z" fill="#F5F5F5" stroke="#1A1A1A" strokeWidth="1.2"/>
    <path d="M14 2v6h6" fill="#E5E5E5" stroke="#1A1A1A" strokeWidth="1.2" strokeLinejoin="round"/>
    <rect x="2" y="13" width="16" height="7" rx="1.5" fill="#1A1A1A"/>
    <path d="M5 15v4m0-4h1.3c1.4 0 1.4 2.2 0 2.2H5" stroke="#fff" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9 15v4m0-4h.8c2 0 2 4 0 4H9" stroke="#fff" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M13.5 15v4m0-4H16m-2.5 2h2" stroke="#fff" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const MarkdownIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="4" width="20" height="16" rx="3" fill="#F5F5F5" stroke="#1A1A1A" strokeWidth="1.2"/>
    <path d="M5 16V8l3 4 3-4v8" stroke="#1A1A1A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M17 10v6m0 0l-2.5-2.5M17 16l2.5-2.5" stroke="#1A1A1A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const DocxIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 2h8l6 6v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2z" fill="#F5F5F5" stroke="#1A1A1A" strokeWidth="1.2"/>
    <path d="M14 2v6h6" fill="#E5E5E5" stroke="#1A1A1A" strokeWidth="1.2" strokeLinejoin="round"/>
    <rect x="1" y="13" width="18" height="7" rx="1.5" fill="#1A1A1A"/>
    <path d="M3.2 15v4m0-4h.6c1.8 0 1.8 4 0 4h-.6" stroke="#fff" strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round"/>
    <ellipse cx="8" cy="17" rx="1.2" ry="2" stroke="#fff" strokeWidth="0.9" fill="none"/>
    <path d="M12.8 15.2c-1-.4-2.2.2-2.2 1.8s1.2 2.2 2.2 1.8" stroke="#fff" strokeWidth="0.9" strokeLinecap="round" fill="none"/>
    <path d="M14.5 15l2.5 4m0-4l-2.5 4" stroke="#fff" strokeWidth="0.9" strokeLinecap="round"/>
  </svg>
)

/** Download format options for the dropdown menu */
const DOWNLOAD_FORMATS: { format: DownloadFormat; label: string; icon: React.ReactNode }[] = [
  { format: 'pdf', label: 'PDF', icon: <PdfIcon /> },
  { format: 'md', label: 'Markdown', icon: <MarkdownIcon /> },
  { format: 'docx', label: 'DOCX', icon: <DocxIcon /> },
]

interface AssistantMessageProps {
  message: UIMessage
  conversationId?: string
  onFeedback?: (rating: number, feedbackMessage?: string, screenshot?: string, userQuestion?: string, assistantAnswer?: string) => void
  onRegenerate?: () => void
  onSendFollowUp?: (question: string) => void
  isStreaming?: boolean
  isLastMessage?: boolean
  onImageClick?: (imageUrl: string) => void
  /** Override the agent display name shown in the badge (e.g. a Digital Coworker's name) */
  agentNameOverride?: string
  /** Override the agent avatar URL (e.g. a Digital Coworker's photo) */
  agentAvatarUrlOverride?: string
  /** Version navigation info (null if no versions exist) */
  versionInfo?: VersionInfo | null
  /** Callback to navigate between versions */
  onNavigateVersion?: (userMsgId: string, direction: 'prev' | 'next') => void
  /** The preceding user question text for Q&A context in rating emails */
  userQuestion?: string
  /** FS-676: answer this message's egress-consent prompt (approve/deny card) */
  onConsentResponse?: (verdict: 'granted' | 'denied') => void
}

export function AssistantMessage({ message, conversationId, onFeedback, onRegenerate, onSendFollowUp, isStreaming, isLastMessage, onImageClick, agentNameOverride, agentAvatarUrlOverride, versionInfo, onNavigateVersion, userQuestion, onConsentResponse }: AssistantMessageProps) {
  const [copied, setCopied] = useState(false)
  const [showReasoning, setShowReasoning] = useState(false)
  const [feedbackLoading, setFeedbackLoading] = useState(false)
  const [showFeedbackModal, setShowFeedbackModal] = useState(false)
  const screenshotPromiseRef = useRef<Promise<string | null>>(Promise.resolve(null))
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [downloadSuccess, setDownloadSuccess] = useState(false)
  const { t } = useTranslation()
  const traceLinkFlag = useApiFeatureFlag('kg_trace_link_enabled', true)

  const agentId = message.metadata?.agentUsed || DEFAULT_AGENT
  const agent = AGENTS[agentId] || AGENTS[DEFAULT_AGENT]
  const colors = AGENT_COLORS[agent.color]

  const displayName = agentNameOverride ?? agent.label
  const displayAvatarUrl = agentAvatarUrlOverride ?? agent.avatarUrl

  // Extract parts by type
  const textParts = message.parts.filter((p): p is TextPart => p.type === 'text')
  const reasoningParts = message.parts.filter((p): p is ReasoningPart => p.type === 'reasoning')
  const fileParts = message.parts.filter((p): p is FilePart => p.type === 'file')
  const toolCallParts = message.parts.filter((p): p is ToolCallPart => p.type === 'tool-call')
  const toolResultParts = message.parts.filter((p): p is ToolResultPart => p.type === 'tool-result')
  const sourceParts = message.parts.filter((p): p is SourcePart => p.type === 'source')

  const textContent = textParts.map((p) => p.text).join('\n\n')
  const reasoningContent = reasoningParts.map((p) => p.text).join('\n\n')

  // RE-ENABLED: Frontend preprocessing as safety net for Error 424 failures
  const processedContent = useMemo(
    () => convertMathNotation(textContent),
    [textContent]
  )

  // Check if there's any visible content to show
  const hasContent = textContent.trim() || reasoningContent || fileParts.length > 0 || toolCallParts.length > 0 || sourceParts.length > 0

  // Don't render message card during streaming if there's no content yet
  // (OrchestrationStatus will be shown instead)
  if (isStreaming && !hasContent) {
    return null
  }

  const handleCopy = async () => {
    try {
      // Convert LaTeX to plain text for clipboard
      const plainText = latexToPlainText(textContent)
      await navigator.clipboard.writeText(plainText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      toast.success(t('chatv2:toasts.copied_to_clipboard'))
    } catch {
      toast.error(t('chatv2:toasts.copy_failed'))
    }
  }

  const handleOpenRatingModal = () => {
    if (!onFeedback || feedbackLoading || !message.id) return
    // Fire-and-forget: start capture, open modal immediately (no lag)
    // Filter out modal overlay (data-screenshot-exclude) + tldraw canvas artifacts
    screenshotPromiseRef.current = import('html-to-image')
      .then(({ toJpeg }) =>
        toJpeg(document.body, {
          quality: 0.6,
          backgroundColor: '#ffffff',
          filter: (node: HTMLElement) => {
            if (node.dataset?.screenshotExclude !== undefined) return false
            const cl = node.classList
            if (!cl) return true
            if (cl.contains('tl-background') || cl.contains('tl-grid') || cl.contains('tl-canvas')) return false
            return true
          },
        }),
      )
      .catch(() => null)
    setShowFeedbackModal(true)
  }

  const handleFeedbackSubmit = async (feedbackMessage: string, rating: number | null) => {
    if (!onFeedback || !rating) return

    const answerText = textContent || undefined

    setFeedbackLoading(true)
    try {
      const screenshot = await screenshotPromiseRef.current
      await onFeedback(rating, feedbackMessage, screenshot || undefined, userQuestion, answerText)
      setShowFeedbackModal(false)
      toast.success(t('chatv2:toasts.feedback_thanks'))
    } catch {
      toast.error(t('chatv2:toasts.feedback_failed'))
    } finally {
      setFeedbackLoading(false)
    }
  }

  const handleFollowUpClick = (question: string) => {
    onSendFollowUp?.(question)
  }

  const handleDownload = async (format: DownloadFormat) => {
    if (!conversationId || !message.id || downloadLoading) return

    setDownloadLoading(true)
    setDownloadSuccess(false)
    try {
      const { blob, filename } = await downloadMessage(conversationId, message.id, format)
      triggerBlobDownload(blob, filename)
      setDownloadSuccess(true)
      toast.success(t('chatv2:toasts.downloaded', { filename }))
      setTimeout(() => setDownloadSuccess(false), 2000)
    } catch {
      toast.error(t('chatv2:toasts.download_failed'))
    } finally {
      setDownloadLoading(false)
    }
  }

  // Get tool result for a tool call
  const getToolResult = (toolCallId: string) => {
    return toolResultParts.find((p) => p.toolCallId === toolCallId)
  }

  return (
    <div className="flex gap-3 items-start max-w-3xl">
      {/* Agent Avatar - Photo */}
      <div className="flex-shrink-0">
        <img
          src={displayAvatarUrl}
          alt={agent.name}
          className="w-10 h-10 rounded-full object-cover"
          loading="lazy"
          decoding="async"
          onError={(e) => {
            // Fallback to letter avatar if image fails
            const target = e.target as HTMLImageElement
            target.style.display = 'none'
            const fallback = target.nextElementSibling as HTMLElement
            if (fallback) fallback.classList.remove('hidden')
          }}
        />
        {/* Fallback letter avatar (hidden by default) */}
        <div
          className={clsx(
            'hidden w-10 h-10 rounded-full',
            'flex items-center justify-center',
            'text-sm font-bold',
            colors.bg,
            colors.text
          )}
        >
          {displayName.charAt(0)}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Agent badge (light pill) - matches Figma */}
        <div className="mb-2 flex items-center gap-2">
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 dark:bg-dark-accent text-gray-800 dark:text-dark-text text-sm font-medium border border-gray-200 dark:border-dark-border">
            {displayName}
          </span>
        </div>

        {/* Message card */}
        <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 p-4">
          {/* Thinking trigger (matches Figma ReasoningTrigger) */}
          {reasoningContent && (
            <div className="mb-3">
              <button
                onClick={() => setShowReasoning(!showReasoning)}
                className="flex items-center gap-2 text-sm hover:opacity-80 transition-opacity"
              >
                <img
                  src="/images/intuai-logo-icon.svg"
                  alt=""
                  className="w-4 h-4"
                  aria-hidden="true"
                />
                <span className={clsx(
                  'text-sm font-medium',
                  isStreaming && !showReasoning
                    ? 'thinking-shimmer'
                    : 'text-orange-600 dark:text-orange-400'
                )}>
                  {t('chatv2:messages.thinking_short', { defaultValue: 'Thinking' })}
                </span>
                {showReasoning ? (
                  <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                )}
              </button>
              {showReasoning && (
                <div className="mt-2 p-3 bg-gray-50 dark:bg-dark-muted rounded-lg text-sm text-gray-600 dark:text-gray-400 italic">
                  {reasoningContent}
                </div>
              )}
            </div>
          )}

        {/* Tool calls */}
        {toolCallParts.length > 0 && (
          <div className="mb-3 space-y-2">
            {toolCallParts.map((toolCall) => {
              const result = getToolResult(toolCall.toolCallId)
              return (
                <div
                  key={toolCall.toolCallId}
                  className="rounded-lg border border-gray-200 dark:border-dark-border overflow-hidden"
                >
                  <div className="px-3 py-2 bg-gray-50 dark:bg-dark-muted border-b border-gray-200 dark:border-dark-border flex items-center gap-2">
                    <Wrench className="h-4 w-4 text-gray-500 dark:text-dark-text-muted" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {toolCall.toolName}
                    </span>
                    {!result && isStreaming && (
                      <Loader2 className="h-3 w-3 animate-spin text-gray-400 dark:text-gray-500 ml-auto" />
                    )}
                  </div>
                  {result && (
                    <div className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 bg-white dark:bg-dark-card">
                      <pre className="whitespace-pre-wrap font-mono text-xs">
                        {typeof result.result === 'string'
                          ? result.result
                          : JSON.stringify(result.result, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* Reasoning Path - thinking steps checklist (hidden during streaming, replaced by OrchestrationStatus) */}
        {message.metadata?.reasoningPath && !isStreaming && (
          <ReasoningPath reasoningPath={message.metadata.reasoningPath} />
        )}

        {/* Main content */}
        <div className="prose prose-sm max-w-none text-gray-900 dark:text-dark-text dark:prose-invert">
          {/* Check for structured response (engineering calculations) */}
          {!isStreaming && extractStructuredResponse(textContent) ? (
            <StructuredResponse content={extractStructuredResponse(textContent)!} />
          ) : (
          <ReactMarkdown
            remarkPlugins={remarkPluginList}
            rehypePlugins={rehypePluginList}
            components={mergeMarkdownComponents({
              // ChatV2-specific code handling for CodeBlock component
              code({ node: _node, className, children, ...props }) {
                // IMPORTANT: `remark-math` represents math as `<code class="language-math math-(inline|display)">...`
                // and `rehype-katex` replaces the children with KaTeX elements. If we route these through our
                // `CodeBlock` renderer (which stringifies children), math shows up as `math math-display` with
                // `[object Object]`. So we must special-case math code nodes and render them directly.
                const isMathCode =
                  !!className &&
                  className.includes('language-math') &&
                  (className.includes('math-inline') || className.includes('math-display'))
                if (isMathCode) {
                  const isDisplay = className.includes('math-display')
                  return isDisplay ? (
                    <div className="my-2 overflow-x-auto">{children}</div>
                  ) : (
                    <span>{children}</span>
                  )
                }

                const match = /language-(\w+)/.exec(className || '')
                const isInline = !match && !className

                // CRITICAL: Exclude math languages from CodeBlock
                // remark-math creates language-math nodes that must be handled by rehype-katex
                // This prevents [object Object] rendering and shows proper KaTeX formulas
                if (match && (match[1] === 'math' || match[1] === 'math-inline' || match[1] === 'math-display')) {
                  return (
                    <code className={className} {...props}>
                      {children}
                    </code>
                  )
                }

                if (isInline) {
                  return (
                    <code
                      className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-dark-accent text-gray-800 dark:text-dark-text text-sm font-mono"
                      {...props}
                    >
                      {children}
                    </code>
                  )
                }

                return (
                  <CodeBlock language={match?.[1]} className={className}>
                    {String(children).replace(/\n$/, '')}
                  </CodeBlock>
                )
              },
              // Tables - ChatV2 styling
              table({ children }) {
                return (
                  <div className="overflow-x-auto my-4">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border border border-gray-200 dark:border-dark-border rounded-lg">
                      {children}
                    </table>
                  </div>
                )
              },
              thead({ children }) {
                return <thead className="bg-gray-50 dark:bg-dark-muted">{children}</thead>
              },
              th({ children }) {
                return (
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                    {children}
                  </th>
                )
              },
              td({ children }) {
                return (
                  <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border-t border-gray-200 dark:border-dark-border">
                    {children}
                  </td>
                )
              },
              // Links - ChatV2 styling with external icon
              a({ href, children }) {
                const isExternal = href?.startsWith('http')
                return (
                  <a
                    href={href}
                    target={isExternal ? '_blank' : undefined}
                    rel={isExternal ? 'noopener noreferrer' : undefined}
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline inline-flex items-center gap-1"
                  >
                    {children}
                    {isExternal && <ExternalLink className="h-3 w-3 inline" />}
                  </a>
                )
              },
              // Lists
              ul({ children }) {
                return <ul className="list-disc list-outside space-y-1 my-2 ml-6">{children}</ul>
              },
              ol({ children }) {
                return <ol className="list-decimal list-outside space-y-1 my-2 ml-6">{children}</ol>
              },
              li({ children }) {
                return <li className="text-gray-700 dark:text-gray-300">{children}</li>
              },
              // Blockquote
              blockquote({ children }) {
                return (
                  <blockquote className="border-l-4 border-gray-200 dark:border-dark-border pl-4 py-1 my-4 text-gray-600 dark:text-gray-400 italic">
                    {children}
                  </blockquote>
                )
              },
              // Horizontal rule
              hr() {
                return <hr className="my-4 border-gray-200 dark:border-dark-border" />
              },
            })}
          >
            {processedContent}
          </ReactMarkdown>
          )}
        </div>

        {/* File attachments (charts) */}
        {fileParts.length > 0 && (
          <div className="mt-4">
            {/* Chart count header */}
            <div className="flex items-center gap-2 mb-3">
              <ImageIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('chatv2:messages.generated_charts', { count: fileParts.length, defaultValue: 'Generated Charts ({{count}}):' })}
              </span>
            </div>

            {/* Chart grid */}
            <div className="grid gap-4 md:grid-cols-2">
              {fileParts.map((file, index) => {
                // Handle image src - data may already include the full data URL
                let imgSrc = ''
                if (file.data) {
                  // Check if data already has the data: prefix (from historian backend)
                  if (file.data.startsWith('data:')) {
                    imgSrc = file.data
                  } else {
                    imgSrc = `data:${file.mediaType || 'image/png'};base64,${file.data}`
                  }
                } else {
                  imgSrc = file.url || ''
                }
                const chartData = message.metadata?.chartData?.[index]
                // The backend charts are standalone HTML documents, not images: an <img> cannot
                // decode one, so it fires onError and shows "Chart not available". They mount in a
                // sandboxed iframe, the same way the legacy chat has always rendered them.
                const isDocument = isHtmlChart(imgSrc, file.mediaType)
                const chartTitle =
                  file.title ||
                  t('chatv2:messages.chart_n', { number: index + 1, defaultValue: 'Chart {{number}}' })

                return (
                  <div key={index} className="border border-gray-200 dark:border-dark-border rounded-lg overflow-hidden bg-white dark:bg-dark-card">
                    <div className="relative">
                      {isDocument ? (
                        // Interactive in place: the chart carries its own zoom/pan controls, so it
                        // needs pointer events rather than a click-through to an image preview.
                        <iframe
                          src={imgSrc}
                          title={chartTitle}
                          className="w-full h-96 block border-0 bg-white dark:bg-dark-card"
                          sandbox="allow-scripts"
                        />
                      ) : (
                        <img
                          src={imgSrc}
                          alt={chartTitle}
                          className="w-full h-auto block max-h-96 object-contain cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => onImageClick?.(imgSrc)}
                          onError={(e) => {
                            // Show fallback on error
                            const target = e.target as HTMLImageElement
                            target.style.display = 'none'
                            const fallback = target.nextElementSibling as HTMLElement
                            if (fallback) fallback.style.display = 'flex'
                          }}
                        />
                      )}
                      {/* Fallback display */}
                      <div className="hidden h-48 items-center justify-center bg-gray-100 dark:bg-dark-accent flex-col text-gray-500 dark:text-dark-text-muted">
                        <ImageIcon className="h-12 w-12 text-gray-400 dark:text-gray-500 mb-2" />
                        <div className="text-sm font-medium">{chartTitle}</div>
                        <div className="text-xs">{t('chatv2:messages.chart_unavailable', { defaultValue: 'Chart not available' })}</div>
                      </div>
                    </div>

                    {/* Chart info footer. The chart names itself in its own heading, so
                        this carries only its weight and the span it covers. */}
                    <div className="p-2">
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          {!!chartData?.size && <>{Math.round(chartData.size / 1024)}KB</>}
                        </div>
                        {chartData?.time_range && (
                          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-dark-text-muted">
                            <Calendar className="h-3 w-3" />
                            <span>{chartData.time_range}</span>
                            {chartData.days_span && (
                              <span className="text-gray-400 dark:text-gray-500">{t('chatv2:messages.days_span', { count: chartData.days_span, defaultValue: '({{count}} days)' })}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Source citations — shared with the V3 typed-parts renderer
            (RenderedAssistantMessage) so every source type shows the
            identical card. See SourcesFooter. */}
        <SourcesFooter sourceParts={sourceParts} />

        {/* Actions - hide while streaming */}
        {!isStreaming && (
          <div className="flex items-center gap-1 mt-3">
            <button
              onClick={handleCopy}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-dark-accent transition-colors"
              title={t('chatv2:messages.copy', { defaultValue: 'Copy' })}
            >
              {copied ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <Copy className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              )}
            </button>

            {/* Download button with format dropdown */}
            {conversationId && textContent.trim() && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    disabled={downloadLoading}
                    className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-dark-accent transition-colors"
                    title={t('chatv2:messages.download_as_file', { defaultValue: 'Download as file' })}
                  >
                    {downloadLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin text-gray-400 dark:text-gray-500" />
                    ) : downloadSuccess ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Download className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  {DOWNLOAD_FORMATS.map(({ format, label, icon }) => (
                    <DropdownMenuItem
                      key={format}
                      onClick={() => handleDownload(format)}
                      className="cursor-pointer"
                    >
                      <span className="mr-2">{icon}</span>
                      {label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Version navigator */}
            {versionInfo && onNavigateVersion && (
              <VersionNavigator
                versionInfo={versionInfo}
                onNavigate={onNavigateVersion}
              />
            )}

            {/* Regenerate button - only on last message */}
            {isLastMessage && onRegenerate && (
              <button
                onClick={onRegenerate}
                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-dark-accent transition-colors"
                title={t('chatv2:messages.regenerate', { defaultValue: 'Regenerate response' })}
              >
                <RefreshCw className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              </button>
            )}

            {/* AGNT-685 forensics deep-link. Only renders when the
                backend echoed an x-correlation-id header AND the
                kg_trace_link_enabled flag is on. Opens in a new tab so
                the chat session is preserved. Engineer-facing — no
                i18n, no telemetry; the kg-trace endpoint hits are
                already logged server-side. */}
            {traceLinkFlag.enabled && message.metadata?.correlationId && (
              <a
                href={`/kg-trace/${encodeURIComponent(message.metadata.correlationId)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="kg-trace-link"
                className="flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-dark-accent transition-colors"
                title="Open agent trace (engineer debug)"
                aria-label="Open agent trace (engineer debug)"
              >
                <Search className="h-4 w-4 text-gray-400 dark:text-gray-500" aria-hidden="true" />
              </a>
            )}

            {onFeedback && (
              <button
                onClick={handleOpenRatingModal}
                disabled={feedbackLoading || !message.id}
                className={clsx(
                  'flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-dark-accent transition-colors',
                  message.metadata?.rating && 'bg-yellow-50 dark:bg-yellow-900/20',
                  feedbackLoading && 'opacity-50'
                )}
                title={message.metadata?.rating
                  ? t('chatv2:feedback.rated_value', { rating: message.metadata.rating, defaultValue: 'Rated {{rating}}/5' })
                  : t('chatv2:feedback.rate_title', { defaultValue: 'Rate this response' })}
              >
                {feedbackLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-gray-400 dark:text-gray-500" />
                ) : (
                  <>
                    <Star
                      className={clsx(
                        'h-4 w-4',
                        message.metadata?.rating
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-400 dark:text-gray-500'
                      )}
                    />
                    {message.metadata?.rating ? (
                      <span className="text-xs text-gray-600 dark:text-gray-400">{message.metadata.rating}/5</span>
                    ) : (
                      <span className="text-xs text-gray-500 dark:text-dark-text-muted">{t('chatv2:feedback.rate', { defaultValue: 'Rate' })}</span>
                    )}
                  </>
                )}
              </button>
            )}
          </div>
        )}
        </div>{/* End of message card */}

        {/* Egress-consent prompt (FS-676): the turn would have used web search
          * but the tenant's `ask` policy has no stored verdict yet. The answer
          * above is the KG-only attempt this card offers to improve on. Gated
          * on !isStreaming (the frame lands milliseconds before the stream's
          * `done`) so Allow can never fire the regenerate flow INTO the still-
          * open stream — same guard FollowUpQuestions uses (greptile #27208). */}
        {message.metadata?.consentRequest && !isStreaming && (
          <ConsentRequestCard
            consentRequest={message.metadata.consentRequest}
            onRespond={onConsentResponse}
          />
        )}

        {/* Follow-up questions - outside the message card, only show on last message */}
        {isLastMessage && (
          <FollowUpQuestions
            questions={message.metadata?.followUpQuestions || []}
            onQuestionClick={handleFollowUpClick}
            isStreaming={isStreaming}
          />
        )}

        {/* Timestamp and response time */}
        {message.createdAt && (
          <span className="mt-1 text-xs text-gray-400 dark:text-gray-500">
            {formatMessageTime(message.createdAt)}
            {message.metadata?.responseDurationMs != null && (() => {
              const rounded = Math.round(message.metadata.responseDurationMs)
              return (
                <>
                  {' · '}
                  {rounded < 1000
                    ? `${rounded}ms`
                    : `${(message.metadata.responseDurationMs / 1000).toFixed(1)}s`}
                </>
              )
            })()}
          </span>
        )}
      </div>

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedbackModal}
        isLoading={feedbackLoading}
        onSubmit={handleFeedbackSubmit}
        onClose={() => setShowFeedbackModal(false)}
        initialRating={message.metadata?.rating ?? null}
      />
    </div>
  )
}

export default AssistantMessage
